import React from 'react';
import { LegalPage } from '../components/legal/LegalPage';
import { Seo } from '../components/Seo';

/**
 * LACloud Support / Help Center. Satisfies the App Store "Support URL"
 * requirement (Guideline 1.5): a real page where users can find answers and a
 * working way to reach us. Reuses the LegalPage chrome so it matches Privacy /
 * Terms. Keep the contact email in sync with what actually forwards to us.
 */
const SUPPORT_EMAIL = 'support@localareacloud.com';

export const Support: React.FC = () => (
  <>
    <Seo
      title="Support — Local Area Cloud"
      description="Get help with Local Area Cloud: contact support, setup help, and answers to common questions about device-to-device file sharing and backup."
      path="/support"
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How do I add another device?',
            acceptedAnswer: {
              '@type': 'Answer',
              text:
                'Install LACloud on the other device and sign in with the same account. Once both devices are signed in and on the same network, they discover each other automatically and appear in your Devices list.',
            },
          },
          {
            '@type': 'Question',
            name: 'How do I contact support?',
            acceptedAnswer: {
              '@type': 'Answer',
              text:
                'Email support@localareacloud.com and we typically reply within 1–2 business days.',
            },
          },
          {
            '@type': 'Question',
            name: 'Are my files stored on your servers?',
            acceptedAnswer: {
              '@type': 'Answer',
              text:
                'No. Files transfer directly between your own devices over your local network. Our servers never see, store, or scan the contents of your files.',
            },
          },
        ],
      }}
    />
    <LegalPage kicker="Help Center" title="Support" effectiveDate="July 22, 2026">
      <div className="l-note">
        <strong>Need a hand?</strong> Email us at{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we&rsquo;ll get back to you,
        usually within 1&ndash;2 business days. The answers below cover the questions we hear most.
      </div>

      <h2>Contact us</h2>
      <p>
        The fastest way to reach a person is email. Tell us your device type (iPhone, iPad, Windows,
        Mac, Linux, or Android), what you were trying to do, and what happened &mdash; a screenshot
        helps a lot.
      </p>
      <ul>
        <li><strong>Support:</strong> <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></li>
        <li><strong>Response time:</strong> typically 1&ndash;2 business days.</li>
        <li><strong>Company:</strong> Chrono Travel LLC, Oregon, USA.</li>
      </ul>

      <h2>Getting started</h2>
      <p>
        LACloud turns the devices you already own into your own private cloud. Install the app on
        each device, sign in with the same account (email, Google, or Microsoft), and your devices
        find each other on your local network. From there you can send files and folders between
        them, browse a device&rsquo;s shared folders, and set up Automatic Backup.
      </p>

      <h2>Frequently asked questions</h2>

      <h3>How do I add another device?</h3>
      <p>
        Install LACloud on the other device and sign in with the same account. When both devices are
        signed in and on the same network, they appear in each other&rsquo;s Devices list
        automatically. If a device doesn&rsquo;t show up, make sure both are on the same Wi-Fi
        network and that the app is open on the device you want to reach.
      </p>

      <h3>Why don&rsquo;t I see my other device?</h3>
      <p>
        Both devices must be signed in to the same account and connected to the same local network,
        and the app must be running on the device you&rsquo;re trying to reach. On iPhone and iPad,
        allow the app&rsquo;s Local Network permission when prompted (you can re-enable it under
        Settings &rsaquo; LACloud &rsaquo; Local Network).
      </p>

      <h3>Where do my files go? Are they on your servers?</h3>
      <p>
        Nowhere but your own devices. Transfers happen device-to-device over your local network. Our
        servers store only your account details and a registry of which devices belong to you &mdash;
        never the contents of your files. See our <a href="/privacy">Privacy Policy</a> for details.
      </p>

      <h3>How does Automatic Backup work?</h3>
      <p>
        Choose a folder and a destination device, pick a daily or weekly schedule, and LACloud
        compresses the folder and copies it to that device on the cadence you set &mdash; an
        always-current mirror you control. Backups are a convenience; always keep an independent copy
        of anything important.
      </p>

      <h3>I forgot my password.</h3>
      <p>
        Use the &ldquo;Set a password&rdquo; / reset option on the sign-in screen, or sign in with
        the Google or Microsoft account you used originally. If you&rsquo;re still stuck, email{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </p>

      <h3>How do I delete my account?</h3>
      <p>
        Open the app&rsquo;s Settings and choose <strong>Delete Account</strong>. This permanently
        removes your account and its device registry from our servers. You can also email{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we&rsquo;ll help.
      </p>

      <h3>Is LACloud free?</h3>
      <p>
        Yes &mdash; the core app is free to use for sharing and backing up files between your devices
        on your local network.
      </p>

      <h2>More resources</h2>
      <ul>
        <li><a href="/privacy">Privacy Policy</a></li>
        <li><a href="/terms">Terms of Service</a></li>
      </ul>
    </LegalPage>
  </>
);

export default Support;
